import { Component, Input, OnChanges, OnInit, SimpleChanges } from '@angular/core';
import { NgbActiveModal } from '@ng-bootstrap/ng-bootstrap';
import { FormArray, FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { NgSelectModule } from '@ng-select/ng-select';
import { Store } from '@ngrx/store';
import { take } from 'rxjs';
import { IRootState } from '../../../app/app.config';
import { ContainerService } from '../../Container/container.service';
import { AbstractContainerJobTypeHelper } from '../../Container/Helper/AbstractContainerJobTypeHelper';
import { ContainerJobItemStatusEnum } from '../../Container/enum/ContainerJobItemStatusEnum';
import { JobTypeEnum } from '../../Container/enum/JobTypeEnum';
import { ContainerMovementListItem } from '../../Container/model/ContainerMovementListItem';
import ContainerUpdateRequest from '../../Container/model/ContainerUpdateRequest';
import ContainerUpdateListItem from '../../Container/model/ContainerUpdateListItem';
import { HazardCode } from '../../Container/model/HazardCode';
import { Port } from '../../Container/model/depots/Port';
import { Division } from '../../divisons/models/Division';
import { selectUserDivision } from '../../user/user.reducer';

@Component({
  selector: 'app-container-update-modal',
  standalone: true,
  imports: [NgSelectModule, ReactiveFormsModule],
  templateUrl: './container-update-modal.component.html',
  styleUrls: ['./container-update-modal.component.scss']
})
export class ContainerUpdateModalComponent implements OnInit, OnChanges {

  @Input()
  containerMovementLists: ContainerMovementListItem[] | [] = [];

  containerUpdateForm!: FormGroup;
  availableMtLocations: Port[] = [];
  userDivision: Division | null = null;
  availableDoorDirections: string[] = [];
  availableHazardCodes: HazardCode[] = [];
  isSaving = false;
  isLoadingContainerGroups = false;
  loadContainerGroupsError = '';
  private containerGroupLoadRequestId = 0;
  
  readonly maxContainerUpdateItems = 25;

  constructor(
    public activeModal: NgbActiveModal,
    private fb: FormBuilder,
    private containerService: ContainerService,
    private readonly store$: Store<IRootState>) {
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['containerMovementLists'] && this.containerUpdateForm) {
      this.createContainerGroupItems();
    }
  }

  ngOnInit() {
    this.availableDoorDirections = this.containerService.getDoorDirections();
    this.containerUpdateForm = this.fb.group({
      containerGroups: this.fb.array([])
    });

    this.createContainerGroupItems();
    this.getHazardCodes();

    this.store$.select(selectUserDivision).pipe(take(1)).subscribe((userDivision) => {
      if (!userDivision) {
        return;
      }

      this.userDivision = {
        name: userDivision.name,
        regionId: userDivision.id
      };

      this.getContainerParks();
    });
  }

  get containerGroups(): FormArray {
    return this.containerUpdateForm.get('containerGroups') as FormArray;
  }

  get hasTooManyContainerUpdates(): boolean {
    return this.containerGroups.length > this.maxContainerUpdateItems;
  }

  get hasNonEditableContainerUpdates(): boolean {
    if (!this.containerUpdateForm) {
      return false;
    }

    return this.containerGroups.controls.some(containerGroup => {
      const jobItemStatusId = this.getNumberValue(containerGroup.get('jobItemStatusId')?.value);
      return jobItemStatusId === ContainerJobItemStatusEnum.Cancelled
        || jobItemStatusId === ContainerJobItemStatusEnum.Completed
        || jobItemStatusId === ContainerJobItemStatusEnum.Billed;
    });
  }

  get canSubmitContainerUpdates(): boolean {
    if (!this.containerUpdateForm) {
      return false;
    }

    return this.containerGroups.length > 0
      && !this.isLoadingContainerGroups
      && this.loadContainerGroupsError.length === 0
      && !this.hasTooManyContainerUpdates
      && !this.hasNonEditableContainerUpdates
      && this.containerUpdateForm.valid
      && this.containerUpdateForm.dirty;
  }

  createContainerGroupItem(containerUpdateItem: ContainerUpdateListItem): FormGroup {
    const emptyDehirePointValue = this.getNullableNumberValue(this.getContainerUpdateValue(containerUpdateItem, 'emptyDehirePointContainerPortId'));
    const canEditEmptyDehirePoint = this.canEditEmptyDehirePoint(containerUpdateItem);

    return this.fb.group({
      containerJobId: this.fb.control({ value: this.getNumberValue(this.getContainerUpdateValue(containerUpdateItem, 'containerJobId')), disabled: true }),
      containerJobItemId: this.fb.control({ value: this.getNumberValue(this.getContainerUpdateValue(containerUpdateItem, 'containerJobItemId')), disabled: true }),
      containerLegId: this.fb.control({ value: this.getNumberValue(this.getContainerUpdateValue(containerUpdateItem, 'containerLegId')), disabled: true }),
      movementId: this.fb.control({ value: this.getNumberValue(this.getContainerUpdateValue(containerUpdateItem, 'movementId')), disabled: true }),
      emptyDehirePointContainerPortId: this.fb.control({ value: this.getNumberValue(this.getContainerUpdateValue(containerUpdateItem, 'emptyDehirePointContainerPortId')), disabled: true }),
      jobItemStatusId: this.fb.control({ value: this.getNumberValue(this.getContainerUpdateValue(containerUpdateItem, 'jobItemStatusId')), disabled: true }),
      jobType: this.fb.control({ value: this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'jobType')), disabled: true }),
      containerJobNumber: this.fb.control({ value: this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'containerJobNumber')), disabled: true }),
      containerNumber: this.fb.control({ value: this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'containerNumber')), disabled: true }),
      orderNumber: this.fb.control(this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'orderNumber'))),
      customerReference: this.fb.control(this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'customerReference'))),
      csrComments: this.fb.control(this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'csrComments'))),
      doorDirection: this.fb.control(this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'doorDirection'))),
      dangerousGoods: this.fb.control(this.getDangerousGoodsValues(this.getContainerUpdateValue(containerUpdateItem, 'dgCode'))),
      sealNumber: this.fb.control(this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'sealNumber'))),
      gridNumber: this.fb.control(this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'gridNumber'))),
      weight: this.fb.control(this.getTextValue(this.getContainerUpdateValue(containerUpdateItem, 'weight')), Validators.pattern(/^\d*$/)),
      emptyDehirePoint: this.fb.control({ value: emptyDehirePointValue, disabled: !canEditEmptyDehirePoint })
    });
  }

  createContainerGroupItems() {
    this.containerGroups.clear();
    this.loadContainerGroupsError = '';

    const containerJobItemIds = this.getSelectedContainerJobItemIds();
    if (containerJobItemIds.length === 0) {
      this.isLoadingContainerGroups = false;
      this.containerUpdateForm.markAsPristine();
      this.containerUpdateForm.markAsUntouched();
      return;
    }

    const loadRequestId = ++this.containerGroupLoadRequestId;
    this.isLoadingContainerGroups = true;

    this.containerService.getContainerUpdateItems(containerJobItemIds)
      .subscribe({
        next: (containerUpdateItems) => {
          if (loadRequestId !== this.containerGroupLoadRequestId) {
            return;
          }

          this.containerGroups.clear();
          containerUpdateItems.forEach((containerUpdateItem) => {
            this.containerGroups.push(this.createContainerGroupItem(containerUpdateItem));
          });

          this.syncDehirePointValuesWithMtLocations();
          this.syncDangerousGoodsValuesWithHazardCodes();
          this.containerUpdateForm.markAsPristine();
          this.containerUpdateForm.markAsUntouched();
        },
        error: () => {
          if (loadRequestId !== this.containerGroupLoadRequestId) {
            return;
          }

          this.containerGroups.clear();
          this.loadContainerGroupsError = 'Failed to load selected container update details.';
          this.isLoadingContainerGroups = false;
        },
        complete: () => {
          if (loadRequestId !== this.containerGroupLoadRequestId) {
            return;
          }

          this.isLoadingContainerGroups = false;
        }
      });
  }

  onSubmit() {
    if (this.isSaving || !this.canSubmitContainerUpdates) {
      return;
    }

    this.isSaving = true;
    this.activeModal.close(this.createContainerUpdateRequest());
  }

  applyToAll(controlName: string) {
    if (this.containerGroups.length <= 1) {
      return;
    }

    const sourceGroup = this.containerGroups.at(0) as FormGroup;
    const sourceControl = sourceGroup.get(controlName);
    if (!sourceControl) {
      return;
    }

    if (sourceControl.disabled) {
      return;
    }

    let hasAppliedChanges = false;
    for (let index = 1; index < this.containerGroups.length; index++) {
      const targetGroup = this.containerGroups.at(index) as FormGroup;
      const targetControl = targetGroup.get(controlName);
      if (targetControl && targetControl.enabled) {
        const sourceValue = sourceControl.value;
        if (!this.areControlValuesEqual(sourceValue, targetControl.value)) {
          if (Array.isArray(sourceValue)) {
            targetControl.patchValue([...sourceValue]);
          } else {
            targetControl.patchValue(sourceValue);
          }

          targetControl.markAsDirty();
          hasAppliedChanges = true;
        }
      }
    }

    if (hasAppliedChanges) {
      this.containerUpdateForm.markAsDirty();
    }
  }

  removeContainerGroup(index: number) {
    if (this.containerGroups.length <= 1) {
      return;
    }

    if (index < 0 || index >= this.containerGroups.length) {
      return;
    }

    this.containerGroups.removeAt(index);
    this.containerUpdateForm.markAsDirty();
  }

  getHazardCodes() {
    this.containerService.getHazardCodes()
      .subscribe(codes => {
        this.availableHazardCodes = codes.sort((firstCode, secondCode) =>
          firstCode.name.localeCompare(secondCode.name)
        );
        this.syncDangerousGoodsValuesWithHazardCodes();
      });
  }

  getContainerParks() {
    if (this.userDivision) {
      this.containerService.getContainerParksByDivision(this.userDivision.regionId)
        .subscribe(ports => {
          this.availableMtLocations = ports;
          this.syncDehirePointValuesWithMtLocations();
        });
    }
  }

  private syncDehirePointValuesWithMtLocations() {
    if (this.availableMtLocations.length === 0) {
      return;
    }

    this.containerGroups.controls.forEach((containerGroup) => {
      const emptyDehirePointContainerPortIdControl = containerGroup.get('emptyDehirePointContainerPortId');
      const dehirePointControl = containerGroup.get('emptyDehirePoint');
      if (!emptyDehirePointContainerPortIdControl || !dehirePointControl) {
        return;
      }

      if (dehirePointControl.disabled) {
        return;
      }

      if (dehirePointControl.dirty) {
        return;
      }

      const dehirePointValue = this.getNullableNumberValue(emptyDehirePointContainerPortIdControl.value);
      if (dehirePointValue === null) {
        return;
      }

      dehirePointControl.patchValue(dehirePointValue, { emitEvent: false });
    });
  }

  private syncDangerousGoodsValuesWithHazardCodes() {
    this.containerGroups.controls.forEach((containerGroup) => {
      const dangerousGoodsControl = containerGroup.get('dangerousGoods');
      if (!dangerousGoodsControl) {
        return;
      }

      const dangerousGoodsValues = dangerousGoodsControl.value;
      if (!Array.isArray(dangerousGoodsValues)) {
        return;
      }

      dangerousGoodsControl.patchValue(
        dangerousGoodsValues.map(value => this.getHazardCodeName(value)),
        { emitEvent: false }
      );
    });
  }

  private getDangerousGoodsValues(value: string | number | null | undefined): string[] {
    const textValue = this.getTextValue(value);
    if (textValue.length === 0) {
      return [];
    }

    return textValue
      .split(',')
      .map(dangerousGoodsValue => dangerousGoodsValue.trim())
      .filter(dangerousGoodsValue => dangerousGoodsValue.length > 0);
  }

  private getHazardCodeName(value: string): string {
    const hazardCode = this.availableHazardCodes.find(code =>
      code.name === value || code.alias === value
    );

    if (hazardCode) {
      return hazardCode.name;
    }

    return value;
  }

  private createContainerUpdateRequest(): ContainerUpdateRequest {
    const containerUpdates = this.containerGroups.getRawValue()
      .map(containerGroup => {
        const canEditEmptyDehirePoint = this.isInboundOrOutboundJobType(containerGroup.jobType);
        const emptyDehirePointContainerPortId = canEditEmptyDehirePoint
          ? this.getNullableNumberValue(containerGroup.emptyDehirePoint)
          : null;
        const emptyDehirePoint = this.getEmptyDehirePointValue(emptyDehirePointContainerPortId);

        return {
          containerJobId: containerGroup.containerJobId,
          containerJobItemId: containerGroup.containerJobItemId,
          containerLegId: containerGroup.containerLegId,
          movementId: containerGroup.movementId,
          orderNumber: this.getTextValue(containerGroup.orderNumber),
          customerReference: this.getTextValue(containerGroup.customerReference),
          csrComments: this.getTextValue(containerGroup.csrComments),
          doorDirection: this.getTextValue(containerGroup.doorDirection),
          dangerousGoodsClasses: this.getStringArray(containerGroup.dangerousGoods),
          sealNumber: this.getTextValue(containerGroup.sealNumber),
          gridNumber: this.getTextValue(containerGroup.gridNumber),
          weightKg: this.getNullableNumberValue(containerGroup.weight),
          emptyDehirePoint: emptyDehirePoint,
          emptyDehirePointContainerPortId: emptyDehirePointContainerPortId
        };
      });

    return {
      containerUpdates: containerUpdates
    };
  }

  private canEditEmptyDehirePoint(containerUpdateItem: ContainerUpdateListItem): boolean {
    return this.isInboundOrOutboundJobType(this.getContainerUpdateValue(containerUpdateItem, 'jobType'));
  }

  private getSelectedContainerJobItemIds(): number[] {
    const containerJobItemIds = this.containerMovementLists
      .map(containerMovementList => this.getNumberValue(this.getContainerMovementValue(containerMovementList, 'containerJobItemId')))
      .filter(containerJobItemId => containerJobItemId > 0);

    return Array.from(new Set(containerJobItemIds));
  }

  private isInboundOrOutboundJobType(value: string | number | null | undefined): boolean {
    const abstractContainerJobTypeHelper: AbstractContainerJobTypeHelper = new AbstractContainerJobTypeHelper();
    const jobType = this.getJobTypeEnum(value);

    if (abstractContainerJobTypeHelper.isInboundJob(jobType)) {
      return true;
    }

    return abstractContainerJobTypeHelper.isOutboundJob(jobType);
  }

  private getJobTypeEnum(value: string | number | null | undefined): JobTypeEnum | null {
    if (typeof value === 'number') {
      if (JobTypeEnum[value] !== undefined) {
        return value as JobTypeEnum;
      }

      return null;
    }

    if (typeof value !== 'string') {
      return null;
    }

    const trimmedValue = value.trim();

    if (trimmedValue.length === 0) {
      return null;
    }

    const numericValue = Number(trimmedValue);

    if (!Number.isNaN(numericValue) && JobTypeEnum[numericValue] !== undefined) {
      return numericValue as JobTypeEnum;
    }

    const normalizedValue = trimmedValue.replace(/\s/g, '').toLowerCase();
    const matchingKey = Object.keys(JobTypeEnum)
      .filter(key => Number.isNaN(Number(key)))
      .find(key => key.toLowerCase() === normalizedValue);

    if (matchingKey === undefined) {
      return null;
    }

    const enumValue = JobTypeEnum[matchingKey as keyof typeof JobTypeEnum];

    if (typeof enumValue === 'number') {
      return enumValue;
    }

    return null;
  }

  private getStringArray(value: string[] | null | undefined): string[] {
    if (!Array.isArray(value)) {
      return [];
    }

    return value
      .map(item => this.getTextValue(item))
      .filter(item => item.length > 0);
  }

  private getEmptyDehirePointValue(containerPortId: number | null): string {
    if (containerPortId === null) {
      return '';
    }

    const mtLocation = this.availableMtLocations.find(location =>
      location.containerPortId === containerPortId
    );

    if (!mtLocation) {
      return '';
    }

    return this.getTextValue(mtLocation.display);
  }

  private getContainerMovementValue(containerMovementList: ContainerMovementListItem, fieldName: keyof ContainerMovementListItem): string | number | null | undefined {
    return this.getModelValue(containerMovementList, fieldName);
  }

  private getContainerUpdateValue(containerUpdateItem: ContainerUpdateListItem, fieldName: keyof ContainerUpdateListItem): string | number | null | undefined {
    return this.getModelValue(containerUpdateItem, fieldName);
  }

  private getModelValue<TModel>(model: TModel, fieldName: keyof TModel): string | number | null | undefined {
    const directValue = model[fieldName] as string | number | null | undefined;
    if (directValue !== null && directValue !== undefined) {
      return directValue;
    }

    const modelRecord = model as TModel & { get?: (fieldName: string) => unknown };
    if (typeof modelRecord.get !== 'function') {
      return undefined;
    }

    const modelValue = modelRecord.get(fieldName.toString());
    if (modelValue === null || modelValue === undefined) {
      return undefined;
    }

    if (typeof modelValue === 'string' || typeof modelValue === 'number') {
      return modelValue;
    }

    return modelValue.toString();
  }

  private getNumberValue(value: string | number | null | undefined): number {
    if (typeof value === 'number') {
      return value;
    }

    if (typeof value === 'string' && value.length > 0) {
      const numberValue = Number(value);
      if (!Number.isNaN(numberValue)) {
        return numberValue;
      }
    }

    return 0;
  }

  private getNullableNumberValue(value: string | number | null | undefined): number | null {
    const numberValue = this.getNumberValue(value);
    if (numberValue === 0) {
      return null;
    }

    return numberValue;
  }

  private areControlValuesEqual(firstValue: string | number | string[] | null | undefined, secondValue: string | number | string[] | null | undefined): boolean {
    if (Array.isArray(firstValue) && Array.isArray(secondValue)) {
      if (firstValue.length !== secondValue.length) {
        return false;
      }

      return firstValue.every((value, index) => value === secondValue[index]);
    }

    if (Array.isArray(firstValue) || Array.isArray(secondValue)) {
      return false;
    }

    return this.getTextValue(firstValue) === this.getTextValue(secondValue);
  }

  private getTextValue(value: string | number | null | undefined): string {
    if (value === null || value === undefined) {
      return '';
    }

    return value.toString();
  }
}
